import React, { useState, useEffect } from "react";
import {
  Box,
  Rating,
  useTheme,
  useMediaQuery,
  TextField,
  IconButton,
  Stack,
  Typography,
  Paper,
  Chip,
  Fade,
  Button,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import CommentIcon from "@mui/icons-material/Comment";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import axios from "axios";
import { API_URL, popupTimer } from "../../consts";
import { toggleLike } from "../../services/likeService";
import { useDispatch } from "react-redux";

const RightInfoPanel = ({ 
  data, 
  onUpdate, 
  authorized, 
  onOpenComments,
  onSpaceClick,
  onBackToAll,
  showBackButton,
  editMode,
  onEditModeChange,
  rightPanelData,
  onRightPanelUpdate
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const [tempSummary, setTempSummary] = useState(data.comment || "");
  const [tempRating, setTempRating] = useState(data?.average_stars || 0);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(data.likes || data.likes_count || 0);
  const [isLiking, setIsLiking] = useState(false);

  // Sync like count when data changes
  useEffect(() => {
    setLikeCount(data.likes || data.likes_count || 0);
  }, [data.likes, data.likes_count]);

  // Sync local state if parent data changes
  useEffect(() => {
    setTempSummary(data.comment || "");
    setTempRating(data?.average_stars || 0);
    // Also update rightPanelData to include rating_id
    onRightPanelUpdate({
      comment: data.comment || "",
      average_stars: data?.average_stars || 0,
      rating_id: data?.rating_id
    });
  }, [data.comment, data.average_stars, data.rating_id]);

  const handleRatingChange = (e, newValue) => {
    setTempRating(newValue);
    onRightPanelUpdate({
      ...rightPanelData,
      average_stars: newValue,
      rating_id: data?.rating_id
    });
  };

  const handleSpaceClick = async (e, ratingId) => {
    e.preventDefault();
    e.stopPropagation();
    onSpaceClick(ratingId);
  };

  const handleBackToAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onBackToAll();
  };

  const handleSummaryChange = (e) => {
    const newValue = e.target.value;
    setTempSummary(newValue);
    onRightPanelUpdate({
      ...rightPanelData,
      comment: newValue,
      rating_id: data?.rating_id
    });
  };

  const handleRatingClick = (e, newValue) => {
    if (!editMode) {
      setTempRating(newValue);
      onRightPanelUpdate({
        ...rightPanelData,
        average_stars: newValue,
        rating_id: data?.rating_id
      });
      onEditModeChange(true);
    }
  };

  // Helper function to get the rating ID for updates
  const getRatingIdForUpdate = () => {
    // If we have a rating_id, use it (for re-rating scenarios)
    if (data?.rating_id) {
      return data.rating_id;
    }
    // Otherwise use the main data id
    return data?.id;
  };

  // Helper function to handle rating updates
  const handleRatingUpdate = async (newRating) => {
    const ratingId = getRatingIdForUpdate();
    if (ratingId) {
      try {
        await onUpdate({
          average_stars: newRating,
          rating_id: ratingId
        });
      } catch (error) {
        console.error("Error updating rating:", error);
      }
    }
  };

  const handleLike = async () => {
    if (!authorized || isLiking || !data.rating_id) return;
    
    setIsLiking(true);
    try {
      const response = await toggleLike({ rating_id: data.rating_id });
      setLikeCount(response.like_count || 0);
      setIsLiked(!isLiked);
    } catch (error) {
      if (error.message === "UNAUTHORIZED") {
        dispatch({ type: "SET_UNAUTHORIZED" });
      } else {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: error?.response?.data?.error || "Failed to like. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      }
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <Fade in timeout={500}>
      <Paper
        elevation={0}
        sx={{
          width: { xs: "100%", sm: "30%", md: "320px" },
          mt: { xs: 0, sm: 0 },
          height: { xs: "auto", sm: "100%" },
          maxHeight: { xs: "50vh", sm: "100%" },
          background: "linear-gradient(145deg, #ffffff, #f8f9fa)",
          borderRadius: { xs: 2, sm: 3 },
          p: { xs: 2, sm: 2.5, md: 3 },
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2, sm: 2.5, md: 3 },
          border: "1px solid rgba(0, 0, 0, 0.05)",
          boxShadow: { xs: "0 2px 8px rgba(0, 0, 0, 0.05)", sm: "0 4px 20px rgba(0, 0, 0, 0.05)" },
          "&::-webkit-scrollbar": {
            width: { xs: "4px", sm: "6px" },
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "3px",
            "&:hover": {
              background: "#555",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: { xs: 1.5, sm: 2 },
            borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: { xs: 1, sm: 2.5 },
          }}
        >
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: { xs: 0.75, sm: 1 },
            flex: { xs: "1 1 100%", sm: "1 1 auto" },
            minWidth: 0,
            flexWrap: "nowrap",
          }}>
            {editMode ? (
              <Rating
                name="editable-rating"
                value={tempRating}
                onChange={handleRatingChange}
                size={isMobile ? "medium" : "large"}
                emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                sx={{ 
                  color: "rgba(255, 204, 72, 1)",
                  "& .MuiRating-icon": {
                    fontSize: { xs: "1.5rem", sm: "1.75rem" },
                  },
                }}
              />
            ) : (
              <Box 
                sx={{ 
                  display: "flex", 
                  alignItems: "center",
                  cursor: "pointer",
                  flexWrap: "nowrap",
                  gap: { xs: 0.5, sm: 0.75 },
                  "&:hover": {
                    opacity: 0.8
                  },
                  "&:active": {
                    opacity: 0.6
                  }
                }}
              >
                <Rating
                  value={data?.average_stars || 0}
                  onChange={(e, newValue) => handleRatingClick(e, newValue)}
                  size={isMobile ? "medium" : "large"}
                  emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                  sx={{ 
                    color: "rgba(255, 204, 72, 1)",
                    "& .MuiRating-icon": {
                      fontSize: { xs: "1.5rem", sm: "1.75rem" },
                    },
                  }}
                />
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {data?.average_stars || 0}
                  <span style={{ color: "grey", fontWeight: 400 }}> / 5</span>
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: { xs: 0.5, sm: 0.5 },
            flexShrink: 0,
            flexWrap: "nowrap",
          }}>
            <IconButton 
              onClick={onOpenComments} 
              size={isMobile ? "medium" : "medium"}
              sx={{
                color: "primary.main",
                minWidth: { xs: "44px", sm: "40px" },
                minHeight: { xs: "44px", sm: "40px" },
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "rgba(18, 141, 111, 0.1)",
                },
                "&:active": {
                  backgroundColor: "rgba(18, 141, 111, 0.2)",
                },
              }}
            >
              <CommentIcon fontSize={isMobile ? "medium" : "medium"} />
            </IconButton>
            <IconButton
              onClick={handleLike}
              disabled={isLiking || !authorized}
              size={isMobile ? "medium" : "medium"}
              sx={{
                color: isLiked ? "error.main" : "text.secondary",
                minWidth: { xs: "44px", sm: "40px" },
                minHeight: { xs: "44px", sm: "40px" },
                flexShrink: 0,
                "&:hover": {
                  color: "error.main",
                  backgroundColor: "rgba(211, 47, 47, 0.1)",
                },
              }}
            >
              {isLiked ? (
                <FavoriteIcon fontSize={isMobile ? "medium" : "medium"} />
              ) : (
                <FavoriteBorderIcon fontSize={isMobile ? "medium" : "medium"} />
              )}
            </IconButton>
            {likeCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minWidth: "fit-content",
                }}
              >
                {likeCount}
              </Typography>
            )}
          </Box>
        </Box>

        {data?.price && (
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                mb: { xs: 0.75, sm: 1 },
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.75, sm: 1 },
                color: "primary.main",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              <AttachMoneyIcon fontSize={isMobile ? "small" : "small"} />
              Price
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontStyle: "italic", 
                opacity: 0.8,
                pl: { xs: 2.5, sm: 3 },
                color: "text.secondary",
                fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
              }}
            >
              {data.currency
                ? `${data.price}${data.currency}`
                : data.price_label}
            </Typography>
          </Box>
        )}

        {(data?.comment || editMode) && (
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                mb: { xs: 0.75, sm: 1 },
                color: "text.primary",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Summary
            </Typography>
            {editMode ? (
              <TextField
                multiline
                rows={isMobile ? 5 : 4}
                fullWidth
                value={tempSummary}
                onChange={handleSummaryChange}
                size={isMobile ? "small" : "medium"}
                sx={{
                  mb: 1,
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    fontSize: { xs: "0.875rem", sm: "0.95rem" },
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            ) : (
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.05)",
                  borderRadius: 2,
                  p: { xs: 1.5, sm: 2 },
                  minHeight: { xs: 80, sm: 100 },
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  backgroundColor: "#fff",
                  color: "text.secondary",
                  lineHeight: { xs: 1.5, sm: 1.6 },
                  wordWrap: "break-word",
                }}
              >
                {data.comment}
              </Paper>
            )}
          </Box>
        )}

        {data?.tags?.length > 0 && (
          <Box>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600, 
                mb: { xs: 0.75, sm: 1 },
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.75, sm: 1 },
                color: "primary.main",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              <LocalOfferIcon fontSize={isMobile ? "small" : "small"} />
              Tags
            </Typography>
            <Box sx={{ 
              display: "flex", 
              flexWrap: "wrap", 
              gap: { xs: 0.75, sm: 1 }, 
              pl: { xs: 2.5, sm: 3 },
            }}>
              {data.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size={isMobile ? "small" : "small"}
                  sx={{
                    backgroundColor: "rgba(18, 141, 111, 0.1)",
                    color: "primary.main",
                    fontWeight: 500,
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                    height: { xs: "28px", sm: "32px" },
                    "&:hover": {
                      backgroundColor: "rgba(18, 141, 111, 0.2)",
                    },
                    "&:active": {
                      backgroundColor: "rgba(18, 141, 111, 0.3)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {(data?.spaces_list?.length > 0 || data?.rating_id) && (
          <Box>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "space-between",
              mb: { xs: 0.75, sm: 1 },
              flexWrap: "wrap",
              gap: { xs: 0.5, sm: 0 },
            }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.75, sm: 1 },
                  color: "primary.main",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
              
                {data?.spaces_list?.length > 0 ? "Collections" : ''}
              </Typography>
              {showBackButton && (
                <IconButton
                  size={isMobile ? "medium" : "small"}
                  onClick={handleBackToAll}
                  sx={{
                    color: "primary.main",
                    minWidth: { xs: "44px", sm: "40px" },
                    minHeight: { xs: "44px", sm: "40px" },
                    "&:hover": {
                      backgroundColor: "rgba(18, 141, 111, 0.1)",
                    },
                    "&:active": {
                      backgroundColor: "rgba(18, 141, 111, 0.2)",
                    },
                  }}
                >
                  <ArrowBackIcon fontSize={isMobile ? "medium" : "small"} />
                </IconButton>
              )}
            </Box>
            <Box sx={{ 
              display: "flex", 
              flexWrap: "wrap", 
              gap: { xs: 0.75, sm: 1 }, 
              pl: { xs: 2.5, sm: 3 },
            }}>
              {data?.spaces_list?.length > 0 ? (
                // Render spaces list if available
                data.spaces_list.map((space) => (
                  <Chip
                    key={space.id}
                    label={space.name}
                    size={isMobile ? "small" : "small"}
                    onClick={(e) => handleSpaceClick(e, space.rating_id)}
                    sx={{
                      backgroundColor: "rgba(18, 141, 111, 0.1)",
                      color: "primary.main",
                      fontWeight: 500,
                      cursor: "pointer",
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                      height: { xs: "28px", sm: "32px" },
                      "&:hover": {
                        backgroundColor: "rgba(18, 141, 111, 0.2)",
                      },
                      "&:active": {
                        backgroundColor: "rgba(18, 141, 111, 0.3)",
                      },
                    }}
                  />
                ))
              ) : null}
            </Box>
          </Box>
        )}
      </Paper>
    </Fade>
  );
};

export default RightInfoPanel;
